import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FileText, Presentation, FileSpreadsheet, Image as ImageIcon,
  FileDown, ExternalLink, Search, Folder, RotateCw, Loader2, Library,
  Eye, Upload, Trash2, X, Share2, Copy, Link as LinkIcon, Check,
  Activity, Globe, Clock, Tag, Tags, Sparkles, FilterX, Package, ListChecks,
  BarChart3, Users,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_ICON = {
  PDF: FileText,
  PowerPoint: Presentation,
  Word: FileText,
  Excel: FileSpreadsheet,
  CSV: FileSpreadsheet,
  Markdown: FileText,
  Text: FileText,
  Image: ImageIcon,
};

const KIND_TINT = {
  PDF: 'text-rose-700 bg-rose-50 border-rose-200',
  PowerPoint: 'text-orange-700 bg-orange-50 border-orange-200',
  Word: 'text-blue-700 bg-blue-50 border-blue-200',
  Excel: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  CSV: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  Markdown: 'text-slate-700 bg-slate-100 border-slate-200',
  Text: 'text-slate-700 bg-slate-100 border-slate-200',
  Image: 'text-violet-700 bg-violet-50 border-violet-200',
};

function formatBytes(b) {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const CollateralLibraryPage = () => {
  const [data, setData] = useState({ categories: [], files: [], known_tags: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [activeKind, setActiveKind] = useState('all');
  const [activeTags, setActiveTags] = useState([]);   // AND-mode
  const [activeScenario, setActiveScenario] = useState(null);
  const [scenarios, setScenarios] = useState([]);

  // Tag editor dialog state
  const [tagFile, setTagFile] = useState(null);
  const [tagDraft, setTagDraft] = useState('');
  const [savingTags, setSavingTags] = useState(false);

  // Bundle creation dialog state
  const [bundleOpen, setBundleOpen] = useState(false);
  const [bundleName, setBundleName] = useState('');
  const [bundleIntro, setBundleIntro] = useState('');
  const [bundleSource, setBundleSource] = useState('scenario'); // 'scenario' | 'filter' | 'snapshot'
  const [bundleScenarioId, setBundleScenarioId] = useState('');
  const [bundleSnapshot, setBundleSnapshot] = useState(false);
  const [bundleExpiry, setBundleExpiry] = useState('30');
  const [creatingBundle, setCreatingBundle] = useState(false);
  const [createdBundleUrl, setCreatedBundleUrl] = useState(null);

  // Bundle list dialog state
  const [bundlesOpen, setBundlesOpen] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [bundlesLoading, setBundlesLoading] = useState(false);
  const [revokingBundle, setRevokingBundle] = useState(null);

  // Bundle analytics dialog
  const [analyticsBundle, setAnalyticsBundle] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Preview dialog state
  const [previewFile, setPreviewFile] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('seertech');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Delete state
  const [deletingId, setDeletingId] = useState(null);

  // Share dialog state
  const [shareFile, setShareFile] = useState(null);
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [shareExpiryDays, setShareExpiryDays] = useState('14');
  const [creatingShare, setCreatingShare] = useState(false);
  const [revokingToken, setRevokingToken] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  // Access log state (lives inside the Share dialog as a second tab)
  const [shareTab, setShareTab] = useState('links'); // 'links' | 'log'
  const [accessLog, setAccessLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const fetchAccessLog = useCallback(async (fileId) => {
    if (!fileId) return;
    setLogLoading(true);
    try {
      const { data: res } = await api.get('/admin/collateral/access-log', {
        params: { file_id: fileId, limit: 100 },
      });
      setAccessLog(res?.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load access log');
    } finally {
      setLogLoading(false);
    }
  }, []);

  const fetchShares = useCallback(async (fileId) => {
    if (!fileId) return;
    setSharesLoading(true);
    try {
      const { data: res } = await api.get('/admin/collateral/share', {
        params: { file_id: fileId },
      });
      setShares(res?.shares || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load share links');
    } finally {
      setSharesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (shareFile) {
      fetchShares(shareFile.id);
      fetchAccessLog(shareFile.id);
      setShareTab('links');
    } else {
      setShares([]);
      setAccessLog([]);
    }
  }, [shareFile, fetchShares, fetchAccessLog]);

  const handleCreateShare = useCallback(async () => {
    if (!shareFile) return;
    setCreatingShare(true);
    try {
      const { data: res } = await api.post('/admin/collateral/share', {
        file_id: shareFile.id,
        expires_in_days: Number(shareExpiryDays),
      });
      toast.success('Share link created');
      // Copy immediately as a convenience.
      try {
        await navigator.clipboard.writeText(res.url);
        setCopiedToken(res.token);
        setTimeout(() => setCopiedToken(null), 2000);
      } catch { /* clipboard may be blocked — that's fine */ }
      fetchShares(shareFile.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create share link');
    } finally {
      setCreatingShare(false);
    }
  }, [shareFile, shareExpiryDays, fetchShares]);

  const handleRevokeShare = useCallback(async (token) => {
    if (!window.confirm('Revoke this link? Anyone with the URL will be locked out immediately.')) return;
    setRevokingToken(token);
    try {
      await api.delete(`/admin/collateral/share/${token}`);
      toast.success('Link revoked');
      if (shareFile) fetchShares(shareFile.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setRevokingToken(null);
    }
  }, [shareFile, fetchShares]);

  const handleCopyShare = useCallback(async (share) => {
    try {
      await navigator.clipboard.writeText(share.url);
      setCopiedToken(share.token);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      toast.error('Clipboard unavailable — copy manually');
    }
  }, []);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/admin/collateral');
      setData(res || { categories: [], files: [], known_tags: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load collateral library');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTagCatalog = useCallback(async () => {
    try {
      const { data: res } = await api.get('/admin/collateral/tag-catalog');
      setScenarios(res?.scenarios || []);
    } catch {
      // Non-fatal — chips still work from data.known_tags.
    }
  }, []);

  useEffect(() => { fetchFiles(); fetchTagCatalog(); }, [fetchFiles, fetchTagCatalog]);

  // Pull plain-text preview content when MD/TXT/CSV is opened.
  useEffect(() => {
    if (!previewFile) { setPreviewText(''); return; }
    const inlineKinds = ['Markdown', 'Text', 'CSV'];
    if (!inlineKinds.includes(previewFile.kind)) { setPreviewText(''); return; }
    let cancelled = false;
    setPreviewLoading(true);
    fetch(`${API_URL}${previewFile.url}`)
      .then((r) => r.text())
      .then((t) => { if (!cancelled) setPreviewText(t.slice(0, 20000)); })
      .catch(() => { if (!cancelled) setPreviewText('(failed to load preview)'); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [previewFile]);

  const handleUpload = useCallback(async () => {
    if (!uploadFile) { toast.error('Pick a file first'); return; }
    const form = new FormData();
    form.append('file', uploadFile);
    form.append('category', uploadCategory);
    setUploading(true);
    try {
      await api.post('/admin/collateral/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${uploadFile.name}`);
      setUploadOpen(false);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchFiles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [uploadFile, uploadCategory, fetchFiles]);

  const handleDelete = useCallback(async (file) => {
    if (!window.confirm(`Delete "${file.filename}"? This cannot be undone.`)) return;
    setDeletingId(file.id);
    try {
      await api.delete(`/admin/collateral/file/${file.id}`);
      toast.success(`Deleted ${file.filename}`);
      fetchFiles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  }, [fetchFiles]);

  const kinds = useMemo(() => {
    const set = new Set();
    (data.files || []).forEach((f) => set.add(f.kind));
    return Array.from(set).sort();
  }, [data.files]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.files || []).filter((f) => {
      if (activeCat !== 'all' && f.category_id !== activeCat) return false;
      if (activeKind !== 'all' && f.kind !== activeKind) return false;
      if (activeTags.length) {
        const fileTags = new Set(f.tags || []);
        for (const t of activeTags) { if (!fileTags.has(t)) return false; }
      }
      if (q && !(`${f.name} ${f.filename} ${(f.tags || []).join(' ')}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [data.files, search, activeCat, activeKind, activeTags]);

  const toggleTag = useCallback((tag) => {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
    setActiveScenario(null);
  }, []);

  const applyScenario = useCallback((scenario) => {
    if (activeScenario === scenario.id) {
      setActiveScenario(null);
      setActiveTags([]);
      return;
    }
    setActiveScenario(scenario.id);
    setActiveTags(scenario.tags || []);
  }, [activeScenario]);

  const clearAllFilters = useCallback(() => {
    setSearch('');
    setActiveCat('all');
    setActiveKind('all');
    setActiveTags([]);
    setActiveScenario(null);
  }, []);

  const openTagEditor = useCallback((f) => {
    setTagFile(f);
    setTagDraft((f.tags || []).join(', '));
  }, []);

  const handleSaveTags = useCallback(async () => {
    if (!tagFile) return;
    const tags = tagDraft
      .split(/[\s,]+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    setSavingTags(true);
    try {
      await api.put(`/admin/collateral/tags/${tagFile.id}`, { tags });
      toast.success(tags.length ? 'Tags updated' : 'Tags reset to defaults');
      setTagFile(null);
      fetchFiles();
      fetchTagCatalog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update tags');
    } finally {
      setSavingTags(false);
    }
  }, [tagFile, tagDraft, fetchFiles, fetchTagCatalog]);

  const openBundleDialog = useCallback(() => {
    setCreatedBundleUrl(null);
    setBundleName('');
    setBundleIntro('');
    setBundleExpiry('30');
    setBundleSnapshot(false);
    // Pre-fill from current state if useful.
    if (activeScenario) {
      const s = scenarios.find((x) => x.id === activeScenario);
      setBundleSource('scenario');
      setBundleScenarioId(activeScenario);
      if (s) setBundleName(`${s.label} pack`);
    } else if (activeTags.length) {
      setBundleSource('filter');
      setBundleScenarioId('');
      setBundleName(`Pack · ${activeTags.join(' + ')}`);
    } else {
      setBundleSource('scenario');
      setBundleScenarioId(scenarios[0]?.id || '');
    }
    setBundleOpen(true);
  }, [activeScenario, activeTags, scenarios]);

  const handleCreateBundle = useCallback(async () => {
    if (!bundleName.trim()) { toast.error('Give the bundle a name'); return; }
    setCreatingBundle(true);
    try {
      let payload = {
        name: bundleName.trim(),
        intro_md: bundleIntro.trim() || null,
        expires_in_days: Number(bundleExpiry),
      };
      if (bundleSnapshot) {
        const ids = (filtered || []).map((f) => f.id);
        if (!ids.length) throw new Error('Snapshot needs at least one file in the current view');
        payload = { ...payload, mode: 'snapshot', file_ids: ids, ordering: ids };
      } else if (bundleSource === 'scenario') {
        const s = scenarios.find((x) => x.id === bundleScenarioId);
        if (!s) throw new Error('Pick a scenario');
        payload = { ...payload, mode: 'tag', tags_all: s.tags || [] };
      } else {
        if (!activeTags.length) throw new Error('No tags active. Switch to scenario or snapshot.');
        payload = { ...payload, mode: 'tag', tags_all: activeTags };
      }
      const { data: res } = await api.post('/admin/collateral/bundle-share', payload);
      setCreatedBundleUrl(res.url);
      try {
        await navigator.clipboard.writeText(res.url);
        toast.success('Bundle URL created & copied');
      } catch {
        toast.success('Bundle URL created');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Bundle create failed');
    } finally {
      setCreatingBundle(false);
    }
  }, [bundleName, bundleIntro, bundleExpiry, bundleSnapshot, bundleSource, bundleScenarioId, scenarios, activeTags, filtered]);

  const fetchBundles = useCallback(async () => {
    setBundlesLoading(true);
    try {
      const { data: res } = await api.get('/admin/collateral/bundle-share');
      setBundles(res?.bundles || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bundles');
    } finally {
      setBundlesLoading(false);
    }
  }, []);

  const openBundlesList = useCallback(() => {
    setBundlesOpen(true);
    fetchBundles();
  }, [fetchBundles]);

  const handleRevokeBundle = useCallback(async (token) => {
    if (!window.confirm('Revoke this bundle? The public link will stop working immediately.')) return;
    setRevokingBundle(token);
    try {
      await api.delete(`/admin/collateral/bundle-share/${token}`);
      toast.success('Bundle revoked');
      fetchBundles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setRevokingBundle(null);
    }
  }, [fetchBundles]);

  const handleCopyBundleUrl = useCallback(async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Clipboard unavailable');
    }
  }, []);

  const openBundleAnalytics = useCallback(async (b) => {
    setAnalyticsBundle(b);
    setAnalytics(null);
    setAnalyticsLoading(true);
    try {
      const { data: res } = await api.get(`/admin/collateral/bundle-share/${b.token}/analytics`);
      setAnalytics(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const total = (data.files || []).length;

  const heroTheme = getLightSectionTheme('admin');

  return (
    <PageShell
      theme={heroTheme}
      hero={(
        <PageHero
          title={(
            <>
              <span className="text-[#394fd4]">Collateral Library</span>
              <span className="text-slate-900"> — Decks, briefs &amp; one-pagers</span>
            </>
          )}
          description="Every sales, board, and explainer asset packaged into the app. Super-admin-only — preview or download any file directly."
          theme={heroTheme}
          testId="collateral-library-hero"
          actions={(
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchFiles}
                disabled={loading}
                className="gap-1.5"
                data-testid="collateral-refresh-btn"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openBundlesList}
                className="gap-1.5"
                data-testid="collateral-bundles-list-btn"
              >
                <ListChecks className="h-3.5 w-3.5" />
                Bundles
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openBundleDialog}
                className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                data-testid="collateral-bundle-create-btn"
              >
                <Package className="h-3.5 w-3.5" />
                Share bundle
              </Button>
              <Button
                size="sm"
                onClick={() => setUploadOpen(true)}
                className="gap-1.5 bg-[#394fd4] hover:bg-[#2b3eb3] text-white"
                data-testid="collateral-upload-open-btn"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload
              </Button>
            </div>
          )}
        />
      )}
    >
      <div className="space-y-6" data-testid="collateral-library-page">
        {/* Filter bar */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative flex-1">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by file name…"
                  className="pl-9"
                  data-testid="collateral-search-input"
                />
              </div>
              <div className="text-sm text-slate-500 whitespace-nowrap">
                Showing <span className="font-semibold text-slate-900" data-testid="collateral-count">{filtered.length}</span> of {total}
              </div>
            </div>

            {/* Category + kind chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-500 mr-1">Category</span>
              <button
                type="button"
                onClick={() => setActiveCat('all')}
                className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                  activeCat === 'all'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid="collateral-cat-all"
              >
                All
              </button>
              {(data.categories || []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCat(c.id)}
                  className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors gap-1 inline-flex items-center ${
                    activeCat === c.id
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`collateral-cat-${c.id}`}
                >
                  <Folder className="h-3 w-3" />
                  {c.label}
                  <span className={`ml-0.5 ${activeCat === c.id ? 'text-slate-300' : 'text-slate-400'}`}>{c.count}</span>
                </button>
              ))}
            </div>

            {kinds.length > 1 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wider font-semibold text-slate-500 mr-1">Type</span>
                <button
                  type="button"
                  onClick={() => setActiveKind('all')}
                  className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                    activeKind === 'all'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid="collateral-kind-all"
                >
                  All
                </button>
                {kinds.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setActiveKind(k)}
                    className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                      activeKind === k
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`collateral-kind-${k}`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            )}

            {/* Scenarios — one-click tag bundles */}
            {scenarios.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wider font-semibold text-slate-500 mr-1 inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Scenario
                </span>
                {scenarios.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => applyScenario(s)}
                    title={s.description}
                    className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                      activeScenario === s.id
                        ? 'bg-[#394fd4] text-white border-[#394fd4]'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`collateral-scenario-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
                {(activeScenario || activeTags.length > 0 || activeCat !== 'all' || activeKind !== 'all' || search) && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="ml-auto px-2.5 py-1 rounded-full border text-xs font-medium border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                    data-testid="collateral-clear-filters"
                  >
                    <FilterX className="h-3 w-3" />
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {/* Tag chip cloud */}
            {(data.known_tags || []).length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs uppercase tracking-wider font-semibold text-slate-500 mr-1 inline-flex items-center gap-1">
                  <Tag className="h-3 w-3" /> Tags
                </span>
                {data.known_tags.map(({ tag, count }) => {
                  const on = activeTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors inline-flex items-center gap-1 ${
                        on
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`collateral-tag-${tag}`}
                    >
                      <span className="font-mono opacity-80">{tag}</span>
                      <span className={on ? 'text-slate-300' : 'text-slate-400'}>{count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* File grid */}
        {loading ? (
          <Card>
            <CardContent className="p-12 flex flex-col items-center justify-center text-slate-500 gap-3">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Loading library…</span>
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-12 flex flex-col items-center justify-center text-slate-500 gap-2">
              <Library className="h-8 w-8 text-slate-300" />
              <span className="text-sm font-medium text-slate-700">No files match</span>
              <span className="text-xs text-slate-500">Try clearing filters or adjusting the search.</span>
            </CardContent>
          </Card>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
            data-testid="collateral-grid"
          >
            {filtered.map((f) => {
              const Icon = KIND_ICON[f.kind] || FileText;
              const tint = KIND_TINT[f.kind] || 'text-slate-700 bg-slate-100 border-slate-200';
              const absUrl = `${API_URL}${f.url}`;
              return (
                <Card
                  key={f.id}
                  className="hover:shadow-md hover:border-slate-300 transition-all"
                  data-testid={`collateral-card-${f.id}`}
                >
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 h-11 w-11 rounded-lg border flex items-center justify-center ${tint}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3
                          className="text-sm font-semibold text-slate-900 truncate"
                          title={f.name}
                        >
                          {f.name}
                        </h3>
                        <p className="text-xs text-slate-500 truncate" title={f.filename}>
                          {f.filename}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center flex-wrap gap-2 text-xs">
                      <Badge variant="outline" className="font-medium uppercase tracking-wide">
                        {f.kind}
                      </Badge>
                      <span className="text-slate-500">{formatBytes(f.size_bytes)}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500">Updated {formatDate(f.modified_at)}</span>
                    </div>

                    {/* Tag chips + Edit affordance */}
                    <div className="flex items-center flex-wrap gap-1" data-testid={`collateral-card-tags-${f.id}`}>
                      {(f.tags || []).slice(0, 6).map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleTag(t)}
                          className={`text-[10px] font-mono px-1.5 py-0.5 rounded border transition-colors ${
                            activeTags.includes(t)
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                          } ${f.tags_are_default ? 'opacity-80' : ''}`}
                          title={f.tags_are_default ? 'Auto-derived tag — click to filter, or edit to override' : 'Click to filter by this tag'}
                        >
                          {t}
                        </button>
                      ))}
                      {(f.tags || []).length > 6 && (
                        <span className="text-[10px] text-slate-400">+{f.tags.length - 6}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => openTagEditor(f)}
                        className="ml-auto text-[10px] text-slate-500 hover:text-indigo-700 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-slate-100"
                        data-testid={`collateral-tag-edit-${f.id}`}
                        title="Edit tags"
                      >
                        <Tags className="h-3 w-3" />
                        {f.tags_are_default ? 'Set tags' : 'Edit'}
                      </button>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        onClick={() => setPreviewFile(f)}
                        className="gap-1.5 flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid={`collateral-preview-${f.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Preview
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        title="Open in a new tab"
                        data-testid={`collateral-view-${f.id}`}
                      >
                        <a href={absUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        title="Download"
                        data-testid={`collateral-download-${f.id}`}
                      >
                        <a href={absUrl} download>
                          <FileDown className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShareFile(f)}
                        className="gap-1.5 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 border-indigo-200"
                        title="Create a public share link"
                        data-testid={`collateral-share-${f.id}`}
                      >
                        <Share2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(f)}
                        disabled={deletingId === f.id}
                        className="gap-1.5 text-rose-700 hover:bg-rose-50 hover:text-rose-800 border-rose-200"
                        title="Delete file"
                        data-testid={`collateral-delete-${f.id}`}
                      >
                        {deletingId === f.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Preview Dialog ───────────────────────────────────────── */}
      <Dialog open={!!previewFile} onOpenChange={(o) => !o && setPreviewFile(null)}>
        <DialogContent
          className="max-w-5xl w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col"
          data-testid="collateral-preview-dialog"
        >
          {previewFile && (
            <>
              <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50 shrink-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <DialogTitle className="text-base font-semibold text-slate-900 truncate">
                      {previewFile.name}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 truncate">
                      {previewFile.filename} · {previewFile.kind} · {formatBytes(previewFile.size_bytes)}
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button asChild variant="outline" size="sm" className="gap-1.5">
                      <a
                        href={`${API_URL}${previewFile.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="collateral-preview-open-tab"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Open
                      </a>
                    </Button>
                    <Button asChild size="sm" className="gap-1.5 bg-[#394fd4] hover:bg-[#2b3eb3] text-white">
                      <a
                        href={`${API_URL}${previewFile.url}`}
                        download
                        data-testid="collateral-preview-download"
                      >
                        <FileDown className="h-3.5 w-3.5" />
                        Download
                      </a>
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 bg-slate-100 overflow-hidden" data-testid="collateral-preview-body">
                {(() => {
                  const absUrl = `${API_URL}${previewFile.url}`;
                  if (previewFile.kind === 'PDF') {
                    return (
                      <iframe
                        src={absUrl}
                        title={previewFile.name}
                        className="w-full h-full min-h-[70vh] bg-white"
                        data-testid="collateral-preview-pdf"
                      />
                    );
                  }
                  if (previewFile.kind === 'Image') {
                    return (
                      <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
                        <img
                          src={absUrl}
                          alt={previewFile.name}
                          className="max-w-full max-h-[80vh] object-contain"
                          data-testid="collateral-preview-image"
                        />
                      </div>
                    );
                  }
                  if (previewFile.kind === 'PowerPoint' || previewFile.kind === 'Word' || previewFile.kind === 'Excel') {
                    // Office Online viewer needs a publicly reachable URL.
                    // Works on the live Seertech domain; on the preview
                    // environment it depends on the ingress being reachable
                    // from Microsoft's servers (usually fine — public host).
                    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(absUrl)}`;
                    return (
                      <div className="w-full h-full flex flex-col">
                        <div className="px-4 py-2 text-[11px] text-slate-500 bg-white border-b border-slate-200 shrink-0">
                          Rendered by Microsoft Office Online. If the preview is blank, open in a new tab.
                        </div>
                        <iframe
                          src={officeUrl}
                          title={previewFile.name}
                          className="flex-1 w-full min-h-[70vh] bg-white"
                          data-testid="collateral-preview-office"
                        />
                      </div>
                    );
                  }
                  if (previewFile.kind === 'Markdown' || previewFile.kind === 'Text' || previewFile.kind === 'CSV') {
                    return (
                      <div className="w-full h-full overflow-auto bg-white p-6">
                        {previewLoading ? (
                          <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                          </div>
                        ) : (
                          <pre
                            className="text-xs leading-relaxed text-slate-800 whitespace-pre-wrap break-words font-mono"
                            data-testid="collateral-preview-text"
                          >
                            {previewText}
                          </pre>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-500 p-8">
                      <Library className="h-8 w-8 text-slate-300" />
                      <span className="text-sm">No inline preview for this file type.</span>
                      <span className="text-xs">Use Open or Download above.</span>
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Bundle Create Dialog ───────────────────────────────── */}
      <Dialog open={bundleOpen} onOpenChange={setBundleOpen}>
        <DialogContent className="max-w-lg" data-testid="collateral-bundle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-4 w-4 text-indigo-600" />
              Share as a bundle
            </DialogTitle>
            <DialogDescription>
              Mint a single public URL that opens a clean landing page with all matching files
              listed in order — perfect for first emails. Tag-driven bundles pick up new
              matching files automatically as you add them.
            </DialogDescription>
          </DialogHeader>

          {createdBundleUrl ? (
            <div className="py-2 space-y-3">
              <p className="text-sm text-slate-700">Your bundle URL is ready:</p>
              <code className="block w-full text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 break-all text-slate-700">
                {createdBundleUrl}
              </code>
              <p className="text-[11px] text-slate-500">
                Copied to your clipboard. Send it as-is in an email — the prospect doesn't need an account.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setBundleOpen(false); }}>Done</Button>
                <Button onClick={() => handleCopyBundleUrl(createdBundleUrl)} className="bg-[#394fd4] hover:bg-[#2b3eb3] text-white gap-1.5">
                  <Copy className="h-3.5 w-3.5" /> Copy again
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Bundle name</label>
                  <Input
                    value={bundleName}
                    onChange={(e) => setBundleName(e.target.value)}
                    placeholder="First email · CHRO"
                    className="mt-1"
                    data-testid="collateral-bundle-name"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                    Intro <span className="text-slate-400 font-normal normal-case">(optional, shown on the landing page)</span>
                  </label>
                  <textarea
                    value={bundleIntro}
                    onChange={(e) => setBundleIntro(e.target.value)}
                    placeholder="A quick read for our next conversation."
                    className="mt-1 w-full min-h-[80px] rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    data-testid="collateral-bundle-intro"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Source</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setBundleSource('scenario')}
                      className={`px-3 py-1.5 rounded-md border text-xs font-medium ${bundleSource === 'scenario' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                      data-testid="collateral-bundle-source-scenario"
                    >Scenario preset</button>
                    <button
                      type="button"
                      onClick={() => setBundleSource('filter')}
                      disabled={activeTags.length === 0}
                      className={`px-3 py-1.5 rounded-md border text-xs font-medium ${bundleSource === 'filter' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'} disabled:opacity-50`}
                      data-testid="collateral-bundle-source-filter"
                      title={activeTags.length === 0 ? 'Set some tag filters first' : 'Use the active tag filter'}
                    >Current tag filter ({activeTags.length})</button>
                  </div>
                </div>

                {bundleSource === 'scenario' && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Scenario</label>
                    <Select value={bundleScenarioId} onValueChange={setBundleScenarioId}>
                      <SelectTrigger className="mt-1" data-testid="collateral-bundle-scenario-select">
                        <SelectValue placeholder="Pick a scenario…" />
                      </SelectTrigger>
                      <SelectContent>
                        {scenarios.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                  <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bundleSnapshot}
                      onChange={(e) => setBundleSnapshot(e.target.checked)}
                      className="mt-0.5"
                      data-testid="collateral-bundle-snapshot"
                    />
                    <span>
                      <span className="font-semibold text-slate-900">Snapshot files now</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        Lock the bundle to the {filtered.length} files currently visible. Future uploads with matching tags <strong>won't</strong> join automatically. Leave off for a dynamic bundle that always reflects your latest tagged content.
                      </span>
                    </span>
                  </label>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Expires in</label>
                  <Select value={bundleExpiry} onValueChange={setBundleExpiry}>
                    <SelectTrigger className="mt-1" data-testid="collateral-bundle-expiry"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days (max)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setBundleOpen(false)} disabled={creatingBundle}>
                  <X className="h-3.5 w-3.5 mr-1.5" />Cancel
                </Button>
                <Button
                  onClick={handleCreateBundle}
                  disabled={creatingBundle}
                  className="bg-[#394fd4] hover:bg-[#2b3eb3] text-white gap-1.5"
                  data-testid="collateral-bundle-submit"
                >
                  {creatingBundle ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5" />}
                  Create &amp; copy URL
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Bundles List Dialog ────────────────────────────────── */}
      <Dialog open={bundlesOpen} onOpenChange={setBundlesOpen}>
        <DialogContent className="max-w-3xl" data-testid="collateral-bundles-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-indigo-600" />
              Shared bundles
            </DialogTitle>
            <DialogDescription>
              All public bundle URLs across the library. Tag-driven bundles update automatically as new matching files are uploaded.
            </DialogDescription>
          </DialogHeader>

          {bundlesLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : bundles.length === 0 ? (
            <div className="text-xs text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-md">
              No bundles yet. Use "Share bundle" to create one.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="collateral-bundles-list">
              {bundles.map((b) => {
                const statusColor = b.status === 'active'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : b.status === 'expired'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-slate-100 text-slate-600 border-slate-200';
                return (
                  <div key={b.token} className="px-4 py-3 space-y-2" data-testid={`collateral-bundle-row-${b.token}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className={`text-[10px] font-semibold uppercase ${statusColor}`}>
                        {b.status}
                      </Badge>
                      <span className="font-semibold text-slate-900 truncate">{b.name}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500">{b.mode === 'tag' ? 'tag-driven' : 'snapshot'}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500">{b.file_count || 0} files</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500">{b.access_count || 0} opens</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 truncate text-slate-700">
                        {b.url}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyBundleUrl(b.url)}
                        disabled={b.status !== 'active'}
                        className="h-7 gap-1 text-xs"
                        data-testid={`collateral-bundle-copy-${b.token}`}
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 text-xs"
                      >
                        <a href={b.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3 w-3" /> Open
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openBundleAnalytics(b)}
                        className="h-7 gap-1 text-xs"
                        data-testid={`collateral-bundle-analytics-${b.token}`}
                      >
                        <BarChart3 className="h-3 w-3" /> Analytics
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevokeBundle(b.token)}
                        disabled={b.status !== 'active' || revokingBundle === b.token}
                        className="h-7 gap-1 text-xs text-rose-700 border-rose-200 hover:bg-rose-50 disabled:opacity-40"
                        data-testid={`collateral-bundle-revoke-${b.token}`}
                      >
                        {revokingBundle === b.token ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        Revoke
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-slate-500">
                      {b.mode === 'tag' && (b.tag_query?.all || []).length > 0 && (
                        <span>tags · {(b.tag_query.all).join(', ')}</span>
                      )}
                      {b.created_by_email && (<>
                        <span className="text-slate-300">·</span>
                        <span>by {b.created_by_email}</span>
                      </>)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBundlesOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Bundle Analytics Dialog ────────────────────────────── */}
      <Dialog open={!!analyticsBundle} onOpenChange={(o) => !o && setAnalyticsBundle(null)}>
        <DialogContent className="max-w-3xl" data-testid="collateral-bundle-analytics-dialog">
          {analyticsBundle && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-indigo-600" />
                  Analytics · {analyticsBundle.name}
                </DialogTitle>
                <DialogDescription>
                  Which assets actually carried the conversation. Use the open-rates to decide what to keep, demote, or replace in the next bundle.
                </DialogDescription>
              </DialogHeader>

              {analyticsLoading || !analytics ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm py-8">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Top-line counters */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bundle-analytics-totals">
                    {[
                      { label: 'Landing opens', value: analytics.totals.bundle_opens, icon: Eye },
                      { label: 'Unique visitors', value: analytics.totals.unique_visitors, icon: Users },
                      { label: 'File opens', value: analytics.totals.file_opens, icon: FileDown },
                      { label: 'Unique file viewers', value: analytics.totals.unique_file_visitors, icon: Globe },
                    ].map(({ label, value, icon: Icon }) => (
                      <div key={label} className="rounded-lg border border-slate-200 bg-white p-3">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                          <Icon className="h-3 w-3" /> {label}
                        </div>
                        <div className="text-2xl font-bold text-slate-900 mt-1 leading-none">{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Per-file open rates */}
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
                      Per-file open rate
                    </div>
                    {(analytics.files || []).length === 0 ? (
                      <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-md">
                        No files currently resolve for this bundle.
                      </div>
                    ) : (
                      <div className="space-y-2" data-testid="bundle-analytics-files">
                        {analytics.files.map((f) => {
                          const pct = Math.max(0, Math.min(100, f.open_rate || 0));
                          const barColor = pct >= 60
                            ? 'bg-emerald-500'
                            : pct >= 30
                              ? 'bg-amber-500'
                              : 'bg-rose-400';
                          return (
                            <div key={f.id} className="border border-slate-200 rounded-md p-3 bg-white">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-semibold text-slate-900 truncate flex-1">{f.name}</span>
                                <Badge variant="outline" className="text-[10px] uppercase">{f.kind}</Badge>
                                <span className="text-slate-500 tabular-nums">{f.opens} opens</span>
                                <span className="text-slate-300">·</span>
                                <span className="text-slate-500 tabular-nums">{f.unique_ips} unique</span>
                                <span className="text-slate-300">·</span>
                                <span className="font-bold text-slate-900 tabular-nums w-12 text-right">{pct.toFixed(0)}%</span>
                              </div>
                              <div className="h-2 mt-2 rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className={`h-full ${barColor} transition-all`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-400 mt-2">
                      Open rate = file opens ÷ landing-page opens. Green &ge; 60%, amber &ge; 30%, red below.
                    </p>
                  </div>

                  {/* Recent events */}
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
                      Recent activity
                    </div>
                    {(analytics.events || []).length === 0 ? (
                      <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-md">
                        No traffic yet on this bundle.
                      </div>
                    ) : (
                      <div className="max-h-56 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
                        {analytics.events.map((e, i) => (
                          <div key={i} className="px-3 py-2 text-[11px] flex items-center gap-2">
                            <Clock className="h-3 w-3 text-slate-400 shrink-0" />
                            <span className="font-medium text-slate-900 tabular-nums">
                              {formatDate(e.accessed_at)}
                            </span>
                            <Badge
                              variant="outline"
                              className={`text-[9px] uppercase ${
                                e.event === 'bundle_open'
                                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              }`}
                            >
                              {e.event === 'bundle_open' ? 'Landing' : 'File'}
                            </Badge>
                            {e.file_id && (
                              <span className="text-slate-700 truncate max-w-[40%]" title={e.file_id}>
                                {e.file_id.split('/').pop()}
                              </span>
                            )}
                            <span className="text-slate-300">·</span>
                            <span className="font-mono text-slate-500">{e.ip || 'unknown'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setAnalyticsBundle(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Tag Editor Dialog ──────────────────────────────────── */}
      <Dialog open={!!tagFile} onOpenChange={(o) => !o && setTagFile(null)}>
        <DialogContent className="max-w-md" data-testid="collateral-tag-dialog">
          {tagFile && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Tags className="h-4 w-4 text-indigo-600" />
                  Tags · {tagFile.name}
                </DialogTitle>
                <DialogDescription>
                  Comma-separated. Use <code className="text-[11px] bg-slate-100 px-1 rounded">key:value</code> pairs (e.g. <code className="text-[11px] bg-slate-100 px-1 rounded">persona:chro</code>, <code className="text-[11px] bg-slate-100 px-1 rounded">stage:outbound</code>). Leave blank to revert to filename-derived defaults.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <textarea
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  className="w-full min-h-[100px] rounded-md border border-slate-200 px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  placeholder="persona:chro, format:script, stage:outbound"
                  data-testid="collateral-tag-input"
                />
                {tagFile.tags_are_default && (
                  <p className="text-[11px] text-slate-500">
                    These tags are currently <strong>auto-derived</strong> from the filename. Saving will pin them.
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setTagDraft(''); }}
                  className="mr-auto text-xs"
                  data-testid="collateral-tag-reset"
                  title="Clear all tags and revert to defaults"
                >
                  Reset to defaults
                </Button>
                <Button variant="outline" onClick={() => setTagFile(null)} disabled={savingTags}>
                  <X className="h-3.5 w-3.5 mr-1.5" />Cancel
                </Button>
                <Button
                  onClick={handleSaveTags}
                  disabled={savingTags}
                  className="bg-[#394fd4] hover:bg-[#2b3eb3] text-white"
                  data-testid="collateral-tag-save"
                >
                  {savingTags ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
                  Save tags
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Share Dialog ────────────────────────────────────────── */}
      <Dialog open={!!shareFile} onOpenChange={(o) => !o && setShareFile(null)}>
        <DialogContent className="max-w-2xl" data-testid="collateral-share-dialog">
          {shareFile && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-indigo-600" />
                  Share &ldquo;{shareFile.name}&rdquo;
                </DialogTitle>
                <DialogDescription>
                  Create a public, time-limited URL anyone can open — no Seertech account needed.
                </DialogDescription>
              </DialogHeader>

              {/* Tabs */}
              <div className="flex items-center gap-1 border-b border-slate-200 -mx-6 px-6">
                <button
                  type="button"
                  onClick={() => setShareTab('links')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    shareTab === 'links'
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="collateral-share-tab-links"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <LinkIcon className="h-3.5 w-3.5" />
                    Links
                    {shares.length > 0 && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{shares.length}</span>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => { setShareTab('log'); fetchAccessLog(shareFile.id); }}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    shareTab === 'log'
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="collateral-share-tab-log"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5" />
                    Access log
                    {accessLog.length > 0 && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{accessLog.length}</span>
                    )}
                  </span>
                </button>
              </div>

              {shareTab === 'links' ? (
                <>
              {/* Create a new link */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Generate new link
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-slate-600">Expires in</label>
                    <Select value={shareExpiryDays} onValueChange={setShareExpiryDays}>
                      <SelectTrigger className="mt-1" data-testid="collateral-share-expiry">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 day</SelectItem>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="90">90 days (max)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleCreateShare}
                    disabled={creatingShare}
                    className="bg-[#394fd4] hover:bg-[#2b3eb3] text-white gap-1.5"
                    data-testid="collateral-share-create"
                  >
                    {creatingShare
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <LinkIcon className="h-3.5 w-3.5" />}
                    Generate &amp; copy
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500">
                  Tip: the link copies to your clipboard automatically on creation.
                </p>
              </div>

              {/* Existing links */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Existing links
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchShares(shareFile.id)}
                    disabled={sharesLoading}
                    className="h-7 gap-1.5 text-xs"
                  >
                    {sharesLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                    Refresh
                  </Button>
                </div>

                {sharesLoading ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                  </div>
                ) : shares.length === 0 ? (
                  <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-md">
                    No share links yet for this file.
                  </div>
                ) : (
                  <div
                    className="max-h-72 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-md"
                    data-testid="collateral-share-list"
                  >
                    {shares.map((s) => {
                      const statusColor = s.status === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : s.status === 'expired'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-slate-100 text-slate-600 border-slate-200';
                      return (
                        <div
                          key={s.token}
                          className="px-3 py-2.5 space-y-1.5"
                          data-testid={`collateral-share-row-${s.token}`}
                        >
                          <div className="flex items-center gap-2 text-xs">
                            <Badge variant="outline" className={`text-[10px] font-semibold uppercase ${statusColor}`}>
                              {s.status}
                            </Badge>
                            <span className="text-slate-500">
                              Expires {formatDate(s.expires_at)}
                            </span>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-500">
                              {s.access_count || 0} {(s.access_count === 1) ? 'view' : 'views'}
                            </span>
                            {s.created_by_email && (
                              <>
                                <span className="text-slate-300">·</span>
                                <span className="text-slate-500 truncate">by {s.created_by_email}</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 truncate text-slate-700">
                              {s.url}
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyShare(s)}
                              disabled={s.status !== 'active'}
                              className="h-7 gap-1 text-xs"
                              data-testid={`collateral-share-copy-${s.token}`}
                            >
                              {copiedToken === s.token
                                ? <><Check className="h-3 w-3" />Copied</>
                                : <><Copy className="h-3 w-3" />Copy</>}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRevokeShare(s.token)}
                              disabled={s.status !== 'active' || revokingToken === s.token}
                              className="h-7 gap-1 text-xs text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800 disabled:opacity-40"
                              data-testid={`collateral-share-revoke-${s.token}`}
                            >
                              {revokingToken === s.token
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <X className="h-3 w-3" />}
                              Revoke
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
                </>
              ) : (
                // ─── Access log tab ─────────────────────────────────────
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Recent hits
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchAccessLog(shareFile.id)}
                      disabled={logLoading}
                      className="h-7 gap-1.5 text-xs"
                      data-testid="collateral-share-log-refresh"
                    >
                      {logLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                      Refresh
                    </Button>
                  </div>

                  {logLoading ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                  ) : accessLog.length === 0 ? (
                    <div className="text-xs text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-md">
                      No one has opened a share link for this file yet.
                      <br />
                      <span className="text-[11px]">Hits show up here within seconds of being opened.</span>
                    </div>
                  ) : (
                    <div
                      className="max-h-80 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-md"
                      data-testid="collateral-share-log-list"
                    >
                      {accessLog.map((ev, idx) => (
                        <div
                          key={`${ev.token}-${ev.accessed_at}-${idx}`}
                          className="px-3 py-2.5 space-y-1"
                          data-testid={`collateral-share-log-row-${idx}`}
                        >
                          <div className="flex items-center gap-2 text-xs">
                            <Clock className="h-3 w-3 text-slate-400 shrink-0" />
                            <span className="font-medium text-slate-900">{formatDate(ev.accessed_at)}</span>
                            <span className="text-slate-300">·</span>
                            <code className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-mono">
                              {ev.token_alias}
                            </code>
                            {ev.note && (
                              <span className="text-slate-500 truncate" title={ev.note}>
                                ({ev.note})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500 pl-5">
                            <Globe className="h-3 w-3 text-slate-400 shrink-0" />
                            <span className="font-mono">{ev.ip || 'unknown'}</span>
                            {ev.user_agent && (
                              <>
                                <span className="text-slate-300">·</span>
                                <span className="truncate" title={ev.user_agent}>
                                  {ev.user_agent}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setShareFile(null)}>
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Upload Dialog ───────────────────────────────────────── */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="collateral-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload to library</DialogTitle>
            <DialogDescription>
              Drop a deck, brief, or PDF here. Max 25 MB. Allowed: PDF, PPTX, DOCX, XLSX, CSV, MD, TXT, PNG, JPG.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Category</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="mt-1" data-testid="collateral-upload-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seertech">Seertech — Sales &amp; Board</SelectItem>
                  <SelectItem value="_root">Reference &amp; General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">File</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                accept=".pdf,.pptx,.docx,.xlsx,.csv,.md,.txt,.png,.jpg,.jpeg"
                className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                data-testid="collateral-upload-file-input"
              />
              {uploadFile && (
                <p className="text-xs text-slate-500 mt-1.5 truncate">
                  Selected: <span className="font-medium text-slate-700">{uploadFile.name}</span>
                  {' · '}{formatBytes(uploadFile.size)}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadOpen(false)}
              disabled={uploading}
              data-testid="collateral-upload-cancel"
            >
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              className="bg-[#394fd4] hover:bg-[#2b3eb3] text-white"
              data-testid="collateral-upload-submit"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default CollateralLibraryPage;
